package fr.frux.core.ads

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp

/**
 * Réclame l'emplacement natif de « Autour de toi » tant que [requested] est vrai (ADR 0030, #530) et
 * dit si l'annonce peut être insérée : claim gagnante de l'arbitrage (priorité devant la bannière,
 * derrière les plein écrans) ET annonce chargée et fraîche. Le chargement ne démarre que lorsque la
 * claim gagne, donc jamais en première session, pour un compte sans pub ni sur une plateforme sans
 * pub. Tant que la réponse est fausse, la liste reste telle quelle : ni trou ni emplacement vide.
 */
@Composable
fun rememberNearbyNativeVisible(requested: Boolean): Boolean {
    val controller = LocalAdsController.current
    val state by controller.state.collectAsState()
    var claimId by remember(controller) { mutableStateOf<Long?>(null) }
    DisposableEffect(controller, requested) {
        if (requested) {
            val claim = controller.claim(AdPlacement.NEARBY_NATIVE)
            claimId = claim.id
            onDispose { claim.release() }
        } else {
            claimId = null
            onDispose {}
        }
    }
    val won = requested && claimId != null && state.visibleClaimId == claimId
    return rememberPlatformNativeReady(active = won) && won
}

/**
 * Carte d'annonce native, à composer seulement quand [rememberNearbyNativeVisible] rend vrai. Fond
 * teinté et bordure la séparent des lignes d'arbre (surface unie sans bordure), la mention
 * « Annonce » et AdChoices vivent dans la vue de la régie. Cibles sans pub : rien n'est composé.
 * [onRemoveAds] : lien « Regarder une vidéo pour 24 h sans pub » sous la carte (#531), si la vidéo est proposable.
 */
@Composable
fun NearbyNativeAd(
    modifier: Modifier = Modifier,
    onRemoveAds: (() -> Unit)? = null,
    removeAdsLoading: Boolean = false,
) {
    Column(modifier = modifier.fillMaxWidth()) {
        Surface(
            modifier = Modifier.fillMaxWidth(),
            shape = MaterialTheme.shapes.large,
            color = MaterialTheme.colorScheme.surfaceVariant,
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline),
        ) {
            PlatformNativeAd(modifier = Modifier.fillMaxWidth())
        }
        if (onRemoveAds != null) {
            RewardedOfferLink(
                onClick = onRemoveAds,
                loading = removeAdsLoading,
                modifier = Modifier.align(Alignment.End),
            )
        }
    }
}

/** Charge l'annonce native tant que [active], dit si une annonce fraîche est prête. Android seulement. */
@Composable
internal expect fun rememberPlatformNativeReady(active: Boolean): Boolean

/** Hébergement de la `NativeAdView` de la régie. */
@Composable
internal expect fun PlatformNativeAd(modifier: Modifier)
